/**
 * FormRow.tsx — 表单行原子（左标签列 + 右控件列，底部分隔线）
 *
 * 移植自原型 design/Novel-admin/src/primitives.jsx 的 window.UI.FormRow，
 * 合并 apps / books / profile / dialogs / dialogs2 全集签名（含 hintFull / required / span）。
 *
 * 各调用方靠传 labelWidth / gap / pad / labelPadTop 还原历史尺寸，逐属性与原型 1:1：
 *   - required   → label 右侧渲染红色星号（var(--neg)）
 *   - hint       → 默认渲染在 label 下方；hintFull=true 时改占满整行（label+控件下方）
 *   - labelWidth → 标签列固定宽（默认 200）
 *   - labelPadTop→ 标签列顶部内边距（控件偏高时对齐用）
 *   - span       → 透传给最外层容器的 gridColumn（栅格里跨列）
 *
 * 注意：ui/MultiLangFormRows.tsx 内部另有一份私有 FormRow（冻结件），与本组件互不影响。
 * 本组件供各 dialog / page 副本收口复用。
 */

import type { CSSProperties, ReactNode } from 'react';

export interface FormRowProps {
  /** 左侧标签文案 */
  label: ReactNode;
  /** 辅助说明：默认在 label 下方；hintFull=true 时占满整行 */
  hint?: ReactNode;
  /** true → hint 渲染在 label+控件下方并占满整行 */
  hintFull?: boolean;
  /** 右侧控件 */
  children: ReactNode;
  /** true → label 右侧渲染红色必填星号 */
  required?: boolean;
  /** 标签列固定宽度（px），默认 200 */
  labelWidth?: number;
  /** 标签列与控件列之间的间距（px），默认 16 */
  gap?: number;
  /** 行内边距，默认 '14px 0' */
  pad?: CSSProperties['padding'];
  /** 标签列顶部内边距（px），用于控件偏高时对齐，默认 0 */
  labelPadTop?: number;
  /** 透传到最外层容器的 gridColumn（在栅格容器里跨列） */
  span?: CSSProperties['gridColumn'];
}

export function FormRow({
  label,
  hint,
  hintFull,
  children,
  required,
  labelWidth = 200,
  gap = 16,
  pad = '14px 0',
  labelPadTop = 0,
  span,
}: FormRowProps) {
  const labelCol = (
    <div style={{ width: labelWidth, flexShrink: 0, paddingTop: labelPadTop || undefined }}>
      <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--fg)' }}>
        {label}
        {required && <span style={{ color: 'var(--neg)', marginLeft: 3 }}>*</span>}
      </div>
      {hint && !hintFull && (
        <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 3, lineHeight: 1.45 }}>
          {hint}
        </div>
      )}
    </div>
  );

  // hintFull：hint 占满整行，渲染在 label+控件下方
  if (hint && hintFull) {
    return (
      <div style={{ padding: pad, borderBottom: '1px solid var(--border)', gridColumn: span }}>
        <div style={{ display: 'flex', gap, alignItems: 'flex-start' }}>
          {labelCol}
          <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 8, lineHeight: 1.5 }}>
          {hint}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        gap,
        padding: pad,
        borderBottom: '1px solid var(--border)',
        alignItems: 'flex-start',
        gridColumn: span,
      }}
    >
      {labelCol}
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}
